"use client";

import React, {
  useState,
  useId,
  useEffect,
  ReactNode,
  KeyboardEvent,
} from "react";
import { Collapse } from "@mui/material";
import clsx from "clsx";
import { TransparentLoading } from "components/TransparentLoading";
import {
  SectionSkeleton,
  TSectionSkeleton,
  sectionSkeletonMinHeight,
} from "components/Section/SectionSkeleton";
import { TSectionLoadingMode } from "components/Section";

export interface CollapsibleSectionProps {
  /** Title displayed at the top of the section */
  title: string;
  /** Section content */
  children: ReactNode;
  /** Whether the section is collapsible */
  collapsible?: boolean;
  /** Default open state (only works when collapsible=true) */
  defaultOpen?: boolean;
  /** Optional class name for wrapper */
  className?: string;
  /** Optional class name for title row */
  headerClassName?: string;
  /** Optional class name for content area */
  contentClassName?: string;
  isLoading?: boolean;
  /** Shape of the loading placeholder — see components/Section. */
  skeleton?: TSectionSkeleton;
  /** See components/Section; defaults to `auto`. */
  loadingMode?: TSectionLoadingMode;
  minHeight?: number | string;
  skeletonRows?: number;
}

/**
 * Mobile variant of CollapsibleSection. Kept visually in step with
 * components/Section — navy title on a pale navy band, led by the orange
 * accent rule on the inline-start edge — with tighter padding for small
 * screens and a larger tap target on the chevron.
 *
 * Loading behaves as it does on desktop, with the skeleton rendered `dense`
 * (fewer rows/bars) to suit the narrower column.
 */
const SectionMobile: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  collapsible = true,
  defaultOpen = true,
  className,
  headerClassName,
  contentClassName,
  isLoading,
  skeleton = "block",
  loadingMode = "auto",
  minHeight,
  skeletonRows,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  const [hasShownContent, setHasShownContent] = useState(!isLoading);
  useEffect(() => {
    if (!isLoading) setHasShownContent(true);
  }, [isLoading]);

  const showSkeleton =
    !!isLoading &&
    loadingMode !== "overlay" &&
    (loadingMode === "skeleton" || !hasShownContent);
  const showOverlay = !!isLoading && !showSkeleton;

  const handleToggle = () => {
    if (collapsible) setOpen((prev) => !prev);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!collapsible) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  const body = (
    <div
      id={contentId}
      className={clsx("p-3", contentClassName)}
      style={{
        minHeight:
          minHeight ??
          (showSkeleton ? sectionSkeletonMinHeight(skeleton) : undefined),
      }}
    >
      {showSkeleton ? (
        <SectionSkeleton
          variant={skeleton}
          minHeight={minHeight}
          rows={skeletonRows}
          dense
        />
      ) : (
        children
      )}
    </div>
  );

  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-lg border border-neutral-30 bg-white",
        className
      )}
    >
      {showOverlay && <TransparentLoading />}

      {/* Header — navy band with the orange accent rule on the leading edge */}
      <div
        className={clsx(
          "flex select-none items-center justify-between gap-3 px-3 py-3",
          "border-s-4 border-s-secondary-70 bg-primary-10 transition-colors duration-200",
          open && "border-b border-b-neutral-30",
          collapsible &&
            "cursor-pointer active:bg-primary-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary-70",
          headerClassName
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? open : undefined}
        aria-controls={collapsible ? contentId : undefined}
      >
        <h2 className="text-sm font-bold text-primary-90">{title}</h2>

        {collapsible && (
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center text-primary-70"
          >
            <i
              className={clsx(
                "fa-solid fa-chevron-down text-xs transition-transform duration-200",
                open && "-rotate-180"
              )}
            />
          </span>
        )}
      </div>

      {/* Content */}
      {collapsible ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {body}
        </Collapse>
      ) : (
        body
      )}
    </div>
  );
};

export default SectionMobile;
